import React from 'react';
import AdminRowIcon from '../../components/admin/AdminRowIcon';
import { notificationsOutline, flashOutline } from 'ionicons/icons';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonList,
  IonItem,
  IonBadge,
  IonButton,
  IonSpinner,
  IonText,
  IonAlert,
  IonSearchbar,
  IonToggle,
} from '@ionic/react';
import { useAdminAlerts } from '../../../application/use-cases/admin/useAdminAlerts';
import { describeCondition } from '../../../domain/Alert';
import AdminListFooter from '../../components/admin/AdminListFooter';
import './Admin.css';

const AdminAlerts: React.FC = () => {
  const {
    tab,
    setTab,
    alerts,
    events,
    onlyTriggered,
    setOnlyTriggered,
    loading,
    error,
    busyId,
    deleteTarget,
    setDeleteTarget,
    handleDelete,
    toggleEnabled,
    restoreAlert,
  } = useAdminAlerts();

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/admin" />
          </IonButtons>
          <IonTitle>Alertas</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={tab} onIonChange={(e) => setTab(e.detail.value as 'configured' | 'events')}>
            <IonSegmentButton value="configured">
              <IonLabel>Configuradas ({alerts.total.toLocaleString('es-AR')})</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="events">
              <IonLabel>Historial ({events.total.toLocaleString('es-AR')})</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
        <IonToolbar>
          {tab === 'configured' ? (
            <IonSearchbar
              value={alerts.query}
              placeholder="Buscar por alerta, estación o dueño"
              onIonInput={(e) => alerts.setQuery(e.detail.value ?? '')}
            />
          ) : (
            <IonSearchbar
              value={events.query}
              placeholder="Buscar por alerta, estación o dueño"
              onIonInput={(e) => events.setQuery(e.detail.value ?? '')}
            />
          )}
          {tab === 'configured' && (
            <label className="admin-filter-toggle" slot="end">
              <IonToggle checked={onlyTriggered} onIonChange={(e) => setOnlyTriggered(e.detail.checked)} />
              Solo disparadas
            </label>
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}
        {error && (
          <IonText color="danger">
            <p className="ion-padding">{error}</p>
          </IonText>
        )}

        {!loading && tab === 'configured' && (
          <IonList className="admin-list">
            {alerts.items.map((a) => (
              <IonItem key={a.id} lines="full">
                <AdminRowIcon icon={notificationsOutline} tone={a.deletedAt ? 'grey' : a.triggered ? 'red' : 'orange'} />
                <IonLabel>
                  <h2>
                    {a.deviceName} — {a.name ?? a.conditions.map((c) => describeCondition(c)).join(' y ')}
                  </h2>
                  <p>
                    {a.ownerName} ({a.ownerEmail})
                  </p>
                  <p className="admin-list-row-meta">
                    {a.enabled ? 'Prendida para el dueño' : 'Apagada para el dueño'} · le avisa a {a.subscriberCount}{' '}
                    usuario{a.subscriberCount === 1 ? '' : 's'}
                  </p>
                  <div className="admin-row-actions">
                    {a.deletedAt ? (
                      <IonButton
                        size="small"
                        fill="clear"
                        color="success"
                        disabled={busyId === a.id}
                        onClick={() => restoreAlert(a.id)}
                      >
                        Restaurar
                      </IonButton>
                    ) : (
                      <>
                        <IonButton
                          size="small"
                          fill="clear"
                          disabled={busyId === a.id}
                          onClick={() => toggleEnabled(a)}
                        >
                          {a.enabled ? 'Apagar para el dueño' : 'Prender para el dueño'}
                        </IonButton>
                        <IonButton
                          size="small"
                          fill="clear"
                          color="danger"
                          disabled={busyId === a.id}
                          onClick={() => setDeleteTarget(a)}
                        >
                          Eliminar
                        </IonButton>
                      </>
                    )}
                  </div>
                </IonLabel>
                {a.deletedAt && <IonBadge color="danger">Eliminada</IonBadge>}
                {!a.deletedAt && a.triggered && <IonBadge color="danger">Disparada</IonBadge>}
              </IonItem>
            ))}
          </IonList>
        )}
        {!loading && tab === 'configured' && (
          <AdminListFooter shown={alerts.items.length} total={alerts.total} loadingMore={alerts.loadingMore} onMore={alerts.loadMore} />
        )}

        {!loading && tab === 'events' && (
          <IonList className="admin-list">
            {events.items.map((e) => (
              <IonItem key={e.id} lines="full">
                <AdminRowIcon icon={flashOutline} tone={e.endedAt ? 'grey' : 'red'} />
                <IonLabel>
                  <h2>
                    {e.deviceName} — {e.alertName ?? e.conditions.map((c) => describeCondition(c)).join(' y ')}
                  </h2>
                  {e.conditions.some((c) => c.value !== null) && (
                    <p>{e.conditions.map((c) => describeCondition(c, c.value)).join(' y ')}</p>
                  )}
                  <p>{e.ownerEmail}</p>
                  <p className="admin-list-row-meta">
                    {new Date(e.startedAt).toLocaleString('es-AR')}
                    {!e.endedAt && ' · en curso'}
                  </p>
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        )}
        {!loading && tab === 'events' && (
          <AdminListFooter shown={events.items.length} total={events.total} loadingMore={events.loadingMore} onMore={events.loadMore} />
        )}

        <IonAlert
          isOpen={!!deleteTarget}
          onDidDismiss={() => setDeleteTarget(null)}
          header="¿Eliminar alerta?"
          message={`Deja de evaluarse para ${deleteTarget?.deviceName}. Se puede restaurar después desde acá.`}
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            { text: 'Eliminar', role: 'destructive', handler: handleDelete },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default AdminAlerts;
